/**
 * Quotes as signed documents: the quote builder, send for signing, signed PDFs, terms, and
 * confirming bookings (with the override when there is no signature).
 */
import { adminBlob, adminFetch } from "./api";
import { PREVIEW } from "./preview";
import { supabase } from "./supabase";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";
const notInPreview = (): never => {
  throw new Error("Not available in preview mode.");
};

export interface DraftLine { code?: string; description: string; quantity: number; unit: string; unitPrice: number }
export interface QuoteDraft {
  customerId?: string;
  leadId?: string;
  clientName: string;
  clientContact?: string;
  clientEmail?: string;
  jobTitle: string;
  siteAddress?: string;
  schedule?: string;
  crewSummary?: string;
  notes?: string;
  validDays: number;
  lines: DraftLine[];
  estimate?: unknown;
}

export interface QuoteDetail {
  id: string; quoteNumber: string; status: string; subtotal: number; gst: number; total: number; validUntil: string | null; notes: string | null;
  jobTitle: string | null; siteAddress: string | null; schedule: string | null; crewSummary: string | null;
  clientName: string | null; clientContact: string | null; clientEmail: string | null; createdAt: string;
  customerId: string | null; leadId: string | null;
  lines: { sortOrder: number; code: string; description: string; quantity: number; unit: string; unitPrice: number; amount: number }[];
}
export interface SigningRequestView {
  id: string; recipientName: string; recipientEmail: string; status: "sent" | "viewed" | "signed" | "expired" | "cancelled";
  expiresAt: string; sentAt: string; viewedAt: string | null; signedAt: string | null; cancelledAt: string | null; expired: boolean;
}
export interface AgreementSummary {
  id: string; signerName: string; signerPosition: string; companyLegalName: string; companyAbn: string; signerEmail: string;
  signedAt: string; signedAtLocal: string; documentSha256: string; hasPdf: boolean;
}
export interface SigningInfo { quote: QuoteDetail; requests: SigningRequestView[]; agreement: AgreementSummary | null }
export interface SendResult { requestId: string; expiresAt: string; link: string; emailed: boolean; emailError: string | null }

export async function buildQuote(d: QuoteDraft): Promise<QuoteDetail> {
  if (PREVIEW) return notInPreview();
  return (await adminFetch<{ quote: QuoteDetail }>("/quotes/build", { method: "POST", body: JSON.stringify(d) })).quote;
}

export async function updateQuoteDraft(id: string, d: QuoteDraft): Promise<{ quote: QuoteDetail; linkCancelled: boolean }> {
  if (PREVIEW) return notInPreview();
  return adminFetch(`/quotes/${id}/draft`, { method: "PUT", body: JSON.stringify(d) });
}

export async function quoteSigning(id: string): Promise<SigningInfo> {
  if (PREVIEW) return notInPreview();
  return adminFetch(`/quotes/${id}/signing`);
}

export async function sendForSigning(id: string, recipientName: string, recipientEmail: string): Promise<SendResult> {
  if (PREVIEW) return notInPreview();
  return adminFetch(`/quotes/${id}/send-for-signing`, { method: "POST", body: JSON.stringify({ recipientName, recipientEmail }) });
}

export async function resendSigning(requestId: string): Promise<SendResult> {
  if (PREVIEW) return notInPreview();
  return adminFetch(`/signing-requests/${requestId}/resend`, { method: "POST", body: "{}" });
}

export async function cancelSigning(requestId: string): Promise<void> {
  if (PREVIEW) return notInPreview();
  await adminFetch(`/signing-requests/${requestId}/cancel`, { method: "POST", body: "{}" });
}

export async function agreementPdf(agreementId: string): Promise<Blob> {
  if (PREVIEW) return notInPreview();
  return adminBlob(`/agreements/${agreementId}/pdf`, {}, { timeoutMs: 90_000 });
}

export async function resendSignedCopy(agreementId: string): Promise<void> {
  if (PREVIEW) return notInPreview();
  await adminFetch(`/agreements/${agreementId}/resend-copy`, { method: "POST", body: "{}" }, { timeoutMs: 90_000 });
}

/** Lines priced from the current rate card for one shift (the public estimator). */
export async function estimateLines(input: { startsAt: string; hours: number; crewSize: number }): Promise<{ lines: DraftLine[]; warnings: string[] }> {
  if (PREVIEW) return notInPreview();
  const res = await fetch(`${API_URL}/quote/estimate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ startsAt: input.startsAt, labour: { hours: input.hours, crewSize: input.crewSize } }),
  });
  const body = (await res.json().catch(() => ({}))) as { lines?: { code: string; description: string; quantity: number; unit: string; unitPrice: number }[]; warnings?: string[]; message?: string };
  if (!res.ok) throw new Error(body.message ?? "The rate card could not price that shift.");
  return { lines: (body.lines ?? []).map((l) => ({ code: l.code, description: l.description, quantity: l.quantity, unit: l.unit, unitPrice: l.unitPrice })), warnings: body.warnings ?? [] };
}

/* ---------- Terms ---------- */

export interface TermsInfo {
  enabled: boolean;
  title: string;
  draft: { markdown: string; sha256: string; alreadyPublished: boolean } | null;
  published: { id: string; versionLabel: string; sha256: string; publishedAt: string; publishedBy: string | null }[];
  current: { id: string; versionLabel: string; contentMd: string; contentSha256: string; publishedAt: string } | null;
}

export async function termsInfo(): Promise<TermsInfo> {
  if (PREVIEW) return notInPreview();
  return adminFetch("/terms");
}

export async function publishTerms(versionLabel: string): Promise<void> {
  if (PREVIEW) return notInPreview();
  await adminFetch("/terms/publish", { method: "POST", body: JSON.stringify({ versionLabel }) });
}

/* ---------- Booking gating ---------- */

/** True when a database error is the "needs a signed agreement" gate. */
export const isAwaitingSignature = (e: unknown) => /AWAITING_SIGNATURE/.test(e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e));

/** Confirm a booking without a signed agreement. The reason, who and when are recorded. */
export async function confirmWithoutSignature(bookingId: string, reason: string, status = "confirmed"): Promise<void> {
  if (PREVIEW) return notInPreview();
  const { error } = await supabase.from("bookings").update({ status, confirm_override_reason: reason.trim() }).eq("id", bookingId);
  if (error) throw error;
}

/** Whether new shifts can start out confirmed: no terms published yet, or a signed agreement on this quote. */
export async function canConfirm(quoteId: string | null): Promise<boolean> {
  if (PREVIEW) return true;
  const { count, error } = await supabase.from("terms_versions").select("id", { count: "exact", head: true });
  if (error) throw error;
  if (!count) return true;
  if (!quoteId) return false;
  const { data, error: e2 } = await supabase.from("agreements").select("id").eq("quote_id", quoteId).limit(1);
  if (e2) throw e2;
  return (data?.length ?? 0) > 0;
}

export async function signedQuoteIds(): Promise<Set<string>> {
  if (PREVIEW) return new Set();
  const { data, error } = await supabase.from("agreements").select("quote_id").eq("kind", "quote_acceptance");
  if (error) throw error;
  return new Set((data as { quote_id: string }[]).map((r) => r.quote_id));
}
